/**
 * Report firing of specific actions to analytics endpoint.
 */
import Cookie from "js-cookie";
import QueryString from "query-string";
import * as actionTypes from "../actions/actionTypes";
import { API } from "@thenounproject/lingo-core";

// Middlewares aren't always called when loading the marketing site
// but we want to store utms before they are removed from the url.
storeUTM();

// Map action type to event name
const actionToInternalEvent = {
  [actionTypes.GENERATE_FILE_CUT_SUCCESS]: (action, { spaceId }) => {
    return {
      event_name: "used_asset",
      data: {
        item_uuid: action.itemId,
        version: action.version,
        dimensions: action.dimensions,
        type: action.fileType,
        space_id: spaceId,
        asset_token: action.shareToken,
      },
    };
  },
  [actionTypes.DOWNLOAD_MULTIPLE_FILES_SUCCESS]: (action, { spaceId }) => {
    return [
      ...action.items.map(i => ({
        event_name: "used_asset",
        data: {
          item_uuid: i.id,
          version: i.version,
          space_id: spaceId,
          asset_token: i.asset ? i.asset.shareToken : null,
        },
      })),
    ];
  },
  [actionTypes.TRACK_COLOR_USAGE]: (action, { spaceId }) => {
    return {
      event_name: "used_asset",
      data: {
        item_uuid: action.itemId,
        version: action.version,
        format: action.format,
        space_id: spaceId,
        asset_token: action.shareToken,
      },
    };
  },
  [actionTypes.SET_NAV_POINT]: ({ navPoint: { spaceId, kitId, version } }, prevNavPoint) => {
    if (!spaceId && !kitId) return;
    if (prevNavPoint && prevNavPoint.spaceId === spaceId && prevNavPoint.kitId === kitId) return;
    return {
      event_name: kitId ? "viewed_kit" : "viewed_space",
      data: {
        space_id: spaceId,
        kit_uuid: kitId,
        version: kitId ? version : undefined,
      },
    };
  },
};

const gtmEvents = [
  actionTypes.SIGNUP_SUCCESS,
  actionTypes.LOGIN_SUCCESS,
  actionTypes.DOWNLOAD_FILE,
  actionTypes.CHANGE_SUBSCRIPTION_SUCCESS,
  actionTypes.GENERATE_FILE_CUT_SUCCESS,
];
function actionToGtmEvent(action, _navPoint) {
  if (!gtmEvents.includes(action.type)) return null;
  const { type, response: _, ...actionData } = action;

  return { event: "Redux Action", type, actionData };
}

const analytics = store => next => action => {
  storeUTM();
  const state = store.getState(),
    navPoint = store.getState().navPoint;
  processAnalytics(action, navPoint || {});
  processIntercomEvent(action, state);
  return next(action);
};

// Store any utms in cookies for use any potential signup later
function storeUTM() {
  const qs = QueryString.parse(window.location.search);
  Object.keys(qs).forEach(k => {
    if (k.startsWith("utm")) {
      Cookie.set(k, qs[k]);
    }
  });
}

function processAnalytics(action, navPoint) {
  const internalEventGetter = actionToInternalEvent[action.type],
    gtmEvent = actionToGtmEvent(action, navPoint);

  if (internalEventGetter) sendEventToLingo(internalEventGetter(action, navPoint));
  if (gtmEvent) sendEventToGTM(gtmEvent);
}

function sendEventToGTM(data) {
  window.dataLayer?.push?.(data);
}

function sendEventToLingo(event) {
  if (!event) return;
  const events = Array.isArray(event) ? event : [event];
  if (!events.length) return;
  API.callApi("analytics/events/", null, "POST", {
    events,
  });
}

function processIntercomEvent(action, state) {
  if (!window.Intercom) return;

  const updates = {};
  if (
    [
      actionTypes.SIGNUP_SUCCESS,
      actionTypes.LOGIN_SUCCESS,
      actionTypes.FETCH_USER_SUCCESS,
    ].includes(action.type)
  ) {
    const { user: user_id, intercom_id } = action.response.result,
      user = action.response.entities.users[user_id];

    updates.user_hash = intercom_id;
    updates.name = user.name;
    updates.email = user.email;
    updates.user_id = String(user.id);
  } else if (action.type === actionTypes.SET_NAV_POINT) {
    const navPoint = action.navPoint;
    const {
        entities: { spaces, kits },
      } = state,
      { kitId, spaceId } = navPoint,
      space = spaceId ? spaces[spaceId] : null,
      spaceRole = space && space.access ? space.access.role : null,
      kit = kitId ? kits[kitId] : null,
      kitRole = kit && kit.access ? kit.access.role : null;
    updates.active_space_role = spaceRole;
    updates.active_kit_role = kitRole;
  }
  if (Object.keys(updates).length) {
    Intercom("update", updates);
  }
}

export default analytics;
